import { useAuth } from '@agentnet/auth';
import { getAgentNetMarketingUrl } from 'api/host-by-env';
import { createPrismUser, getPrismRouteName } from 'api/prismPropertyProfile/prismProperty-profile-api';
import { PrismUserProfile } from 'api/prismPropertyProfile/types';
import { getStateOptions } from 'api/profile/profile-utilities';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useContext } from 'react';
import states from './../ui-kit/inputs/StateSelectField/states.json';

export const useAnmRedirect = () => {
  const { getAccessToken, account } = useAuth();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm } = profileCtx;

  const claims = account?.idTokenClaims as any;
  const isEmployee = claims?.onpremADusername != null;
  const agentNetMarketingUrl = isEmployee ? getAgentNetMarketingUrl() + 'b2e' : getAgentNetMarketingUrl();

  const ensurePrismUserExists = async (profile: PrismUserProfile): Promise<boolean> => {
    const stateOptions = userFirm?.offices ? getStateOptions(userFirm) : [];
    const officeAddress = userFirm?.offices?.find(
      (office: any) => office.isDefault && office.status === 'Active',
    )?.address;

    let names = stateOptions.map((option) => option.name)?.join(',');
    if (!names || names.length === 0) {
      // If no state options and no firmId, use all state abbreviations
      if (!userFirm?.firmId) {
        names = states.map((state) => state.abbreviation).join(',');
      }
    }

    try {
      const updatedPrismProfile: PrismUserProfile = {
        ...profile,
        availableLocations: names,
        firmName: userFirm?.name ?? '',
        officeAddress1: officeAddress?.address1 ?? '',
        officeAddress2: officeAddress?.address2 ?? '',
        officeCity: officeAddress?.city ?? '',
        officeState: officeAddress?.state ?? '',
        officeZipCode: officeAddress?.postalCode ?? '',
      };

      const token = await getAccessToken();
      const response = await createPrismUser(updatedPrismProfile, token);

      if (response?.Error) {
        return false;
      }

      return response?.UserId > 0;
    } catch (error) {
      console.error('Error in ensurePrismUserExists:', error);
      return false;
    }
  };

  const prismRouteName = async (firmId: number): Promise<string> => {
    if (!firmId || firmId <= 0) {
      return '';
    }

    try {
      const token = await getAccessToken();
      const response = await getPrismRouteName(firmId, token);
      const route = response && typeof response === 'string' ? response.trim() : '';
      return route;
    } catch (error) {
      console.error('Error getting prismRouteName:', error);
      return '';
    }
  };

  const redirectToAnm = async (target: '_blank' | '_self' = '_self') => {
    if (!profile?.userId) return;

    try {
      const prismUser: PrismUserProfile = {
        firstName: profile?.firstName ?? '',
        lastName: profile?.lastName ?? '',
        email: profile?.emailAddress ?? '',
        primaryPhone: profile?.phoneNumber ? profile?.phoneNumber.substring(profile?.phoneNumber.length - 10) : '',
        idaaSguid: profile?.azureObjectId ?? '',
        idaaSuserId: profile?.login ?? '',
        idaaStype: 'B2C',
      };

      const isStaffUser = isEmployee || [2, 3, 5, 13].includes(profile?.roleId ?? 0);
      if (isStaffUser) {
        await ensurePrismUserExists(prismUser);
        openWindow(agentNetMarketingUrl, target);
      } else {
        const prismRoute = await prismRouteName(userFirm?.firmId ? Number(userFirm.firmId) : 0);
        if (prismRoute && prismRoute.length > 0) {
          const anmAgentUrl = getAgentNetMarketingUrl(prismRoute);
          openWindow(anmAgentUrl, target);
        } else {
          await ensurePrismUserExists(prismUser);
          openWindow(agentNetMarketingUrl, target);
        }
      }
    } catch (error) {
      console.error('Error in ANM redirect:', error);
      openWindow(agentNetMarketingUrl, target);
    }
  };

  const openWindow = (url: string, target: '_blank' | '_self' = '_self') => {
    console.log('hook Redirecting to ANM URL:', url);
    if (target === '_blank') {
      window.open(url, '_blank');
    } else {
      window.location.href = url;
    }
  };

  return { redirectToAnm, ensurePrismUserExists };
};
